import { arcgisToGeoJSON } from "@esri/arcgis-to-geojson-utils";
import { loadModules } from "esri-loader";
import { get, writable } from "svelte/store";
import Analytics from "../../Analytics/Analytics";
import AuthStore from "../../Auth/Store";
import LoadState from "../../Enums/LoadState";
import GeoHelper from "../../Helpers/GeoHelper";
import MapLayerGroup from "../../Map/Enums/MapLayerGroup";
import VisibilityState from "../../Map/Enums/VisibilityState";
import MapLayerManager from "../../Map/MapLayerManager";
import { Paint } from "../../Map/MapLayerStyles";
import MapStore from "../../Map/Store";
import MaritimeBoundary from "./MaritimeBoundary";

// TODO: this func is same in UKHOcharts store
const initLayerData = () => {
	return {
		layers: [],
		visible: writable(true)
	};
};
const MaritimeBoundariesStore = {
	config: {
		server: "https://services1.arcgis.com/ZURaVO35aTK1Ugmq/arcgis/rest/services",
		eez: "/eez_v10/FeatureServer",
		ttw: "/eez_12NM/FeatureServer"
	},
	meta: {
		loadState: writable(LoadState.INIT),
		layers: [],
		layerData: {
			esri_eez: initLayerData(),
			esri_ttw: initLayerData()
		},
		visible: writable(false),
		esri: {
			classes: {
				Map: null,
				FeatureLayer: null,
				IdentityManager: null,
				Query: null,
				Point: null,
				SpatialReference: null
			},
			objects: {
				layers: {
					eez: null,
					ttw: null
				}
			}
		},
		lastUpdated: writable(0)
	},
	filters: {
		filterString: writable("")
	},
	results: {
		boundaries: writable([])
	},
	spatialQuery: {
		meta: {
			loadState: writable(LoadState.INIT),
			error: writable("")
		},
		results: {
			boundaries: writable([])
		}
	},
	actions: {
		init: () => {
			if (get(MaritimeBoundariesStore.meta.loadState) !== LoadState.INIT) {
				return;
			}
			MaritimeBoundariesStore.meta.loadState.set(LoadState.LOADING);
			console.log("Loading Maritime Boundaries...");

			loadModules(["esri/Map", "esri/layers/FeatureLayer", "esri/identity/IdentityManager", "esri/tasks/support/Query", "esri/geometry/Point", "esri/geometry/SpatialReference"])
				.then((classes) => {
					MaritimeBoundariesStore.meta.esri.classes.Map = classes[0];
					MaritimeBoundariesStore.meta.esri.classes.FeatureLayer = classes[1];
					MaritimeBoundariesStore.meta.esri.classes.IdentityManager = classes[2];
					MaritimeBoundariesStore.meta.esri.classes.Query = classes[3];
					MaritimeBoundariesStore.meta.esri.classes.Point = classes[4];
					MaritimeBoundariesStore.meta.esri.classes.SpatialReference = classes[5];

					const FeatureLayer = classes[1];
					const Query = classes[3];

					const eezUrl = MaritimeBoundariesStore.config.server + MaritimeBoundariesStore.config.eez;
					const eezLayer = new FeatureLayer({ url: eezUrl, layerId: 0 });
					MaritimeBoundariesStore.meta.esri.objects.layers.eez = eezLayer;

					const ttwUrl = MaritimeBoundariesStore.config.server + MaritimeBoundariesStore.config.ttw;
					const ttwLayer = new FeatureLayer({ url: ttwUrl, layerId: 0 });
					MaritimeBoundariesStore.meta.esri.objects.layers.ttw = ttwLayer;

					const query = new Query();
					query.returnGeometry = false;
					query.where = "1 = 1";
					query.outFields = [
						"GeoName",
						"x_1",
						"y_1",
						"FID",
						"ISO_Ter1",
						"Territory1"
					];

					Promise.all([
						eezLayer.queryFeatures(query),
						ttwLayer.queryFeatures(query)
					]).then((responses) => {
						const map = MapStore.helpers.getMap();
						const boundaries = [];
						const eezData = responses[0];
						for (let i = 0; i < eezData.features.length; i++) {
							const feature = arcgisToGeoJSON(eezData.features[i]);
							const boundary = new MaritimeBoundary(feature, "EEZ");
							boundaries.push(boundary);
						}

						const ttwData = responses[1];
						for (let i = 0; i < ttwData.features.length; i++) {
							const feature = arcgisToGeoJSON(ttwData.features[i]);
							const boundary = new MaritimeBoundary(feature, "TTW");
							boundaries.push(boundary);
						}

						MaritimeBoundariesStore.helpers._addDataLayer("esri_eez", "fill", Paint.eez, GeoHelper.emptyGeoJson());
						MaritimeBoundariesStore.helpers._addDataLayer("esri_ttw", "fill", Paint.ttw, GeoHelper.emptyGeoJson());
						MaritimeBoundariesStore.meta.layerData.esri_eez.layers.push("esri_eez");
						MaritimeBoundariesStore.meta.layerData.esri_ttw.layers.push("esri_ttw");

						MapLayerManager.addLayerGroup(MapLayerGroup.MARITIME_BOUNDARIES, ["esri_eez", "esri_ttw"], ["esri_eez", "esri_ttw"]);

						MaritimeBoundariesStore.results.boundaries.set(boundaries);
						MaritimeBoundariesStore.meta.loadState.set(LoadState.LOADED);

						MaritimeBoundariesStore.meta.lastUpdated.set(Date.now());
					});
				});
		},
		spatialQuery: (lngLatArray) => {
			MaritimeBoundariesStore.spatialQuery.meta.error.set("");
			MaritimeBoundariesStore.spatialQuery.meta.loadState.set(LoadState.LOADING);

			const SpatialReference = MaritimeBoundariesStore.meta.esri.classes.SpatialReference;
			const Point = MaritimeBoundariesStore.meta.esri.classes.Point;
			const Query = MaritimeBoundariesStore.meta.esri.classes.Query;

			const point = new Point({
				longitude: lngLatArray[0],
				latitude: lngLatArray[1],
				spatialReference: SpatialReference.WebMercator// WGS84 // WebMercator
			});

			MaritimeBoundariesStore.meta.esri.objects.layers.eez.createQuery();
			const query1 = new Query({
				returnGeometry: false,
				outFields: [
					"GeoName",
					"x_1",
					"y_1",
					"FID",
					"ISO_Ter1",
					"Territory1"
				],
				geometryPrecision: 3,
				spatialRelationship: "intersects",
				geometry: point,
				num: 5
			});
			const query2 = query1.clone();

			const queries = [
				MaritimeBoundariesStore.meta.esri.objects.layers.eez.queryFeatures(query1),
				MaritimeBoundariesStore.meta.esri.objects.layers.ttw.queryFeatures(query2)
			];
			Promise.all(queries)
				.then((responses) => {
					const eezResponse = responses[0];
					const ttwResponse = responses[1];

					const allEEZResultIds = eezResponse.features.map((item) => {
						return item.attributes.FID;
					});
					const allTTWResultIds = ttwResponse.features.map((item) => {
						return item.attributes.FID;
					});
					const allBoundaries = get(MaritimeBoundariesStore.results.boundaries);
					const filteredBoundaries = allBoundaries.filter((boundary) => {
						return (boundary.type === "EEZ" && allEEZResultIds.indexOf(boundary.esriId) >= 0) ||
							   (boundary.type === "TTW" && allTTWResultIds.indexOf(boundary.esriId) >= 0);
					});

					MaritimeBoundariesStore.spatialQuery.results.boundaries.set(filteredBoundaries);
					MaritimeBoundariesStore.spatialQuery.meta.loadState.set(LoadState.LOADED);
				})
				.catch((error) => {
					MaritimeBoundariesStore.spatialQuery.meta.error.set(error.message);
					MaritimeBoundariesStore.spatialQuery.meta.loadState.set(LoadState.INIT);
				});
		},
		updateVisibility: () => {
			const map = MapStore.helpers.getMap();
			if (map) {
				if (get(MaritimeBoundariesStore.meta.loadState) !== LoadState.LOADED) {
					return;
				}
				const visible = get(MaritimeBoundariesStore.meta.visible);
				const dataLayers = Object.keys(MaritimeBoundariesStore.meta.layerData);
				for (let i = 0; i < dataLayers.length; i++) {
					const layer = MaritimeBoundariesStore.meta.layerData[dataLayers[i]];
					for (let j = 0; j < layer.layers.length; j++) {
						const layerVisible = get(layer.visible);
						map.setLayoutProperty(layer.layers[j], "visibility", (visible && layerVisible) ? VisibilityState.VISIBLE : VisibilityState.NONE);
					}
				}
			}
		},
		updateFeatureCollection: () => {
			const featureCollection = {
				EEZ: GeoHelper.emptyGeoJson(),
				TTW: GeoHelper.emptyGeoJson()
			};
			const boundaries = get(MaritimeBoundariesStore.results.boundaries);
			for (let i = 0; i < boundaries.length; i++) {
				if (get(boundaries[i].visible) &&
					get(boundaries[i].loadState) === LoadState.LOADED) {
					featureCollection[boundaries[i].type].features.push(boundaries[i].asFeature());
				}
			}
			const map = MapStore.helpers.getMap();
			if (map) {
				const sourceEEZ = map.getSource("esri_eez");
				const sourceTTW = map.getSource("esri_ttw");
				if (sourceEEZ) {
					sourceEEZ.setData(featureCollection.EEZ);
				}
				if (sourceTTW) {
					sourceTTW.setData(featureCollection.TTW);
				}
			}
		},
		loadSingle: (boundaryModelObject) => {
			Analytics.event("load_" + boundaryModelObject.esriId, { category: "Maritime Boundaries", username: get(AuthStore.userInfo).username, id: boundaryModelObject.esriId });

			boundaryModelObject.loadState.set(LoadState.LOADING);
			MaritimeBoundariesStore.meta.lastUpdated.set(Date.now());

			const layerType = boundaryModelObject.type.toLowerCase();
			const layer = MaritimeBoundariesStore.meta.esri.objects.layers[layerType];
			const Query = MaritimeBoundariesStore.meta.esri.classes.Query;
			const SpatialReference = MaritimeBoundariesStore.meta.esri.classes.SpatialReference;

			const query = new Query();
			query.returnGeometry = true;
			query.outFields = ["FID"];
			query.geometryPrecision = 3;
			query.where = "FID = '" + boundaryModelObject.esriId + "'";
			query.outSpatialReference = SpatialReference.WGS84;

			Promise.all([
				layer.queryFeatures(query)
			]).then((responses) => {
				console.log("response", responses);
				const feature = arcgisToGeoJSON(responses[0].features[0]);
				const newFeature = feature; // GeoHelper.toWGS84(layer, feature);
				boundaryModelObject.data.geometry = newFeature.geometry;
				boundaryModelObject.loadState.set(LoadState.LOADED);

				MaritimeBoundariesStore.actions.updateFeatureCollection();
				MaritimeBoundariesStore.meta.lastUpdated.set(Date.now());
			});
		}
	},
	helpers: {
		// TODO: consider moving to a map helper.
		_addDataLayer: (name, type, paint, featureCollection) => {
			MapStore.helpers.addDataLayer(name, type, {}, paint, featureCollection, MaritimeBoundariesStore.meta.layers);
		}
	}
};

export default MaritimeBoundariesStore;
